import { motion, useReducedMotion } from 'framer-motion'
import { useRef } from 'react'
import { Footer } from '@/components/Footer'
import { Navbar } from '@/components/Navbar'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Spotlight } from '@/components/ui/spotlight'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

const milestones = [
    { mark: 'TurboRAG', note: 'Sub-100ms retrieval with exact context windows. Production recall for systems where latency and accuracy both matter.' },
    { mark: 'A/MCL', note: 'Automatic memory for AI agents. Persistent context across Cursor, Claude Code, Codex — zero-config continuity.' },
    { mark: 'Skill Composer', note: 'Auto-chaining AI skills into pipelines. One install across 12+ agents. Zero runtime cost.' },
]

export function AboutPage() {
    const ref = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()
    const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

    return (
        <div ref={ref} className="min-h-screen bg-[#211d18] text-[#eee8dc]">

            <Navbar variant="dark" />

            {/* Hero */}
            <section className="relative flex min-h-[92svh] items-center overflow-hidden pt-28 sm:pt-36">
                <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="#6e1f1b" />
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.035) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.025) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
                <div className="absolute bottom-0 left-0 right-0 h-52 bg-gradient-to-t from-[#211d18] to-transparent" />

                <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-8">
                    <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={m(0.08)} className="mb-6 font-body text-[11px] uppercase tracking-[0.3em] text-[#c8a88a]/45">
                        About the lab
                    </motion.p>

                    <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={m(0.14)} className="max-w-[1000px] font-serif text-[clamp(3.2rem,8vw,8.5rem)] font-medium leading-[0.86]">
                        <span className="text-[#eee8dc]">One person.</span>
                        <br />
                        <span className="font-serif italic text-[#c8a88a]">Frontier output.</span>
                    </motion.h1>

                    <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={m(0.28)} className="mt-10 max-w-xl font-body text-[17px] leading-[1.85] text-[#eee8dc]/40">
                        Jpdz Labs is a single-person research lab building AI infrastructure and original models. No team. No investors. Just taste, speed, and an unreasonable commitment to shipping.
                    </motion.p>

                    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1, delay: 0.5, ease }} className="mt-12 h-[2px] w-24 origin-left bg-[#6e1f1b]" />
                </div>
            </section>

            {/* Thesis */}
            <section className="relative border-t border-[#eee8dc]/[0.04] py-28 sm:py-36">
                <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
                    <div className="grid gap-16 lg:grid-cols-[1fr_1.2fr]">
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, ease }}>
                            <p className="mb-4 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]">Thesis</p>
                            <h2 className="font-serif text-[clamp(2.8rem,5.5vw,5.5rem)] font-medium leading-[0.88] text-[#eee8dc]">
                                Build what<br /><span className="italic text-[#c8a88a]">matters.</span>
                            </h2>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, delay: 0.06, ease }} className="lg:pt-4">
                            <p className="mb-5 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                Most AI tooling is built for demos, not production. The gap between "cool prototype" and "thing developers actually use" is where Jpdz Labs operates.
                            </p>
                            <p className="mb-5 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                Every product ships with a simple test: does this remove real friction, or does it just look impressive? If it doesn't pass, it doesn't ship.
                            </p>
                            <p className="font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                The latest frontier: <span className="text-[#c8a88a]">PKM</span> — a language model built from scratch with a novel architecture. Not a fine-tune. Not a wrapper. Original model research from a single-person lab.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="relative border-t border-[#eee8dc]/[0.04] bg-[#1a1614] py-28 sm:py-36">
                <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.02) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.015) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />

                <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, ease }} className="mb-20 max-w-2xl">
                        <p className="mb-4 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]">What's shipped</p>
                        <h2 className="font-serif text-[clamp(2.8rem,5.5vw,5.5rem)] font-medium leading-[0.9] text-[#eee8dc]">
                            The<br className="hidden sm:block" />
                            <span className="italic text-[#c8a88a]">stack.</span>
                        </h2>
                    </motion.div>

                    <div className="space-y-0">
                        {milestones.map((ms, i) => (
                            <motion.div
                                key={ms.mark}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.55, delay: i * 0.04, ease }}
                                className="group grid items-start gap-6 border-t border-[#eee8dc]/[0.05] py-10 sm:grid-cols-[200px_1fr] sm:gap-12"
                            >
                                <div className="flex items-center gap-4">
                                    <span className="font-serif text-[11px] font-medium uppercase tracking-[0.2em] text-[#eee8dc]/20">0{i + 1}</span>
                                    <h3 className="font-serif text-[1.6rem] leading-tight text-[#eee8dc] transition-colors duration-300 group-hover:text-[#c8a88a]">{ms.mark}</h3>
                                </div>
                                <p className="max-w-lg font-body text-[15px] leading-[1.8] text-[#eee8dc]/35">{ms.note}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PKM highlight */}
            <section className="relative overflow-hidden border-t border-[#eee8dc]/[0.04] py-28 sm:py-36">
                <div className="pointer-events-none absolute right-[-4%] top-1/2 hidden -translate-y-1/2 select-none font-serif text-[20vw] leading-none text-[#eee8dc]/[0.015] lg:block" aria-hidden="true">P</div>

                <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
                    <div className="grid gap-16 lg:grid-cols-[1fr_1.2fr]">
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, ease }}>
                            <p className="mb-4 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]">Current frontier</p>
                            <h2 className="font-serif text-[clamp(2.8rem,5.5vw,5.5rem)] font-medium leading-[0.88] text-[#eee8dc]">
                                PKM
                            </h2>
                            <p className="mt-3 font-serif text-xl italic text-[#c8a88a]/60">Novel architecture. Built from scratch.</p>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, delay: 0.06, ease }} className="lg:pt-4">
                            <p className="mb-5 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                PKM is a language model designed from the ground up — not a fine-tune, not a LoRA, not a wrapper around someone else's weights. Original architecture, original training, original research.
                            </p>
                            <p className="mb-5 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                The goal: prove that a single person with taste and conviction can push the frontier of model design. Not by scaling compute, but by rethinking structure.
                            </p>
                            <p className="font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                It's looking good. More details when it's ready to ship — the lab doesn't announce, it releases.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative overflow-hidden bg-[#6e1f1b] py-24 sm:py-32">
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(55deg, transparent, transparent 40px, rgba(238,232,220,0.14) 40px, rgba(238,232,220,0.14) 41px)' }} />
                <div className="absolute left-0 right-0 top-0 h-16 bg-gradient-to-b from-[#211d18]/25 to-transparent" />

                <div className="relative mx-auto max-w-[1400px] px-4 text-center sm:px-8">
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }}>
                        <p className="mb-4 font-body text-[11px] uppercase tracking-[0.3em] text-[#eee8dc]/25">Get in touch</p>
                        <h2 className="mb-6 font-serif text-[clamp(2.4rem,5vw,5rem)] font-medium leading-[0.92] text-[#eee8dc]">Want to collaborate?</h2>
                        <a href="mailto:ratnam@jpdz.app" className="group inline-flex min-h-[48px] items-center gap-3 font-body text-[17px] text-[#eee8dc]/55 transition-colors duration-300 hover:text-[#eee8dc]">
                            <span className="relative">ratnam@jpdz.app<span className="absolute -bottom-1 left-0 h-px w-0 bg-[#eee8dc]/30 transition-all duration-500 group-hover:w-full" /></span>
                            <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                    </motion.div>
                </div>
            </section>

            <Footer variant="dark" />
        </div>
    )
}
